import React, { useState, useEffect } from 'react';
import { useParams, Link as ReactRouterLink } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useDisclosure,
} from '@chakra-ui/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Breadcrumbs from './shared/Breadcrumbs';
import LoadingSpinner from './shared/LoadingSpinner';
import GenerateTrancheModal from './GenerateTrancheModal';
import fetchWithAuth from '../utils/fetchWithAuth';

function LivePollingDashboard() {
  const { pollId } = useParams();
  const [poll, setPoll] = useState(null);
  const [audience, setAudience] = useState(null);
  const [progress, setProgress] = useState(null);
  const [demographics, setDemographics] = useState(null);
  const [tranches, setTranches] = useState([]);
  const [respondents, setRespondents] = useState([]);
  const [abResults, setAbResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRespondents, setShowRespondents] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue('background.shade1');
  const boxBgColor = useColorModeValue('background.shade2');
  const borderColor = useColorModeValue('gray.300');

  useEffect(() => {
    fetchAllData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchAllData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      const [pollResponse, progressResponse, demographicsResponse, tranchesResponse] =
        await Promise.all([
          fetchWithAuth(`/api/polls/${pollId}/`),
          fetchWithAuth(`/api/polls/${pollId}/progress/`),
          fetchWithAuth(`/api/polls/${pollId}/demographics/`),
          fetchWithAuth(`/api/polls/${pollId}/tranches/`),
        ]);

      if (!pollResponse.ok) throw new Error('Failed to fetch poll');

      const pollData = await pollResponse.json();
      setPoll(pollData);

      // Get audience for this poll
      if (pollData.audiences && pollData.audiences.length > 0) {
        const audienceResponse = await fetchWithAuth(`/api/audiences/${pollData.audiences[0]}/`);
        if (audienceResponse.ok) {
          const audienceData = await audienceResponse.json();
          setAudience(audienceData);
        }
      }

      if (progressResponse.ok) {
        const progressData = await progressResponse.json();
        setProgress(progressData);
      }

      if (demographicsResponse.ok) {
        const demographicsData = await demographicsResponse.json();
        setDemographics(demographicsData);
      }

      if (tranchesResponse.ok) {
        const tranchesData = await tranchesResponse.json();
        setTranches(tranchesData.tranches || []);
      }

      // Fetch A/B results if poll has variants
      if (pollData.invitation_variants && pollData.invitation_variants.length > 0) {
        try {
          const abResponse = await fetchWithAuth(`/api/polls/${pollId}/ab-results/`);
          if (abResponse.ok) {
            setAbResults(await abResponse.json());
          }
        } catch (e) {
          console.error('Failed to fetch A/B results:', e);
        }
      }

      setError(null);

      // Also refresh respondents if they're being shown
      if (showRespondents) {
        await fetchRespondents();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const prepareComparisonData = () => {
    if (!demographics || !poll?.target_demographics || demographics.total_respondents === 0) {
      return [];
    }

    const data = [];

    Object.keys(poll.target_demographics).forEach((fieldName) => {
      const fieldConfig = poll.target_demographics[fieldName];

      // Handle nested structure: {enabled: bool, targets: {value: pct}}
      const targets = fieldConfig?.targets || fieldConfig;
      const enabled = fieldConfig?.enabled !== undefined ? fieldConfig.enabled : true;

      if (!enabled || !targets || typeof targets !== 'object') return;

      const currentDist = demographics.demographics[fieldName] || [];

      Object.keys(targets).forEach((value) => {
        const currentItem = currentDist.find((item) => item.value === value);
        data.push({
          group: `${fieldName}: ${value}`,
          target: targets[value],
          current: currentItem ? currentItem.percentage : 0,
        });
      });
    });

    return data;
  };

  const handleTrancheGenerated = () => {
    fetchAllData();
    if (showRespondents) {
      fetchRespondents();
    }
  };

  const fetchRespondents = async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/respondents/`);
      if (response.ok) {
        const data = await response.json();
        // Handle different response formats
        if (Array.isArray(data)) {
          setRespondents(data);
        } else if (data.respondents && Array.isArray(data.respondents)) {
          setRespondents(data.respondents);
        } else if (data.results && Array.isArray(data.results)) {
          setRespondents(data.results);
        } else {
          console.error('Unexpected respondents data format:', data);
          setRespondents([]);
        }
      }
    } catch (err) {
      console.error('Error fetching respondents:', err);
      setRespondents([]);
    }
  };

  const handleToggleRespondents = () => {
    if (!showRespondents) {
      fetchRespondents();
    }
    setShowRespondents(!showRespondents);
  };

  if (loading && !poll) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Box bg={bgColor} minHeight="100vh" py={8}>
        <Box maxWidth="800px" margin="0 auto" textAlign="center">
          <Text color="red.500">Error: {error}</Text>
          <Button as={ReactRouterLink} to="/pollsPage" mt={4}>
            Back to Polls
          </Button>
        </Box>
      </Box>
    );
  }

  if (!poll) {
    return (
      <Box bg={bgColor} minHeight="100vh" py={8}>
        <Text textAlign="center">Poll not found</Text>
      </Box>
    );
  }

  const comparisonData = prepareComparisonData();

  return (
    <Box bg={bgColor} minHeight="100vh" py={8}>
      <Box maxWidth="1200px" margin="0 auto" px={4}>
        <Breadcrumbs
          items={[
            { label: 'Home', path: '/' },
            { label: 'Polls', path: '/pollsPage' },
            { label: poll.title, path: `/manage-questions/${pollId}` },
            { label: 'Live Dashboard' },
          ]}
        />

        {/* Header */}
        <HStack justify="space-between" mb={6}>
          <VStack align="start">
            <Heading as="h1" size="xl">
              Live Poll Dashboard
            </Heading>
            <Text fontSize="lg" color="gray.600">
              {poll.title}
            </Text>
          </VStack>
          <HStack spacing={2}>
            <Button
              as={ReactRouterLink}
              to={`/analytics/${pollId}`}
              colorScheme="purple"
              size="sm"
            >
              Analytics
            </Button>
            <Button onClick={fetchAllData} size="sm" isLoading={loading}>
              Refresh
            </Button>
            {poll.state === 'live' && (
              <Button onClick={onOpen} colorScheme="blue" size="sm">
                Generate Next Tranche
              </Button>
            )}
          </HStack>
        </HStack>

        {/* Progress Stats */}
        {progress && (
          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4} mb={6}>
            <Stat bg={boxBgColor} p={4} borderRadius="md" border="1px solid" borderColor={borderColor}>
              <StatLabel>Total Sent</StatLabel>
              <StatNumber>{progress.total_sent}</StatNumber>
              <StatHelpText>Survey invitations</StatHelpText>
            </Stat>
            <Stat bg={boxBgColor} p={4} borderRadius="md" border="1px solid" borderColor={borderColor}>
              <StatLabel>Responses</StatLabel>
              <StatNumber>{progress.total_completed}</StatNumber>
              <StatHelpText>{progress.completion_rate}% completion rate</StatHelpText>
            </Stat>
            <Stat bg={boxBgColor} p={4} borderRadius="md" border="1px solid" borderColor={borderColor}>
              <StatLabel>Goal</StatLabel>
              <StatNumber>{progress.response_goal || 'N/A'}</StatNumber>
              <StatHelpText>
                {progress.goal_met ? '✓ Goal Met' : `${progress.progress_percentage}% complete`}
              </StatHelpText>
            </Stat>
            <Stat bg={boxBgColor} p={4} borderRadius="md" border="1px solid" borderColor={borderColor}>
              <StatLabel>Status</StatLabel>
              <StatNumber fontSize="lg">
                <Badge colorScheme={poll.state === 'live' ? 'green' : 'gray'}>
                  {poll.state.toUpperCase()}
                </Badge>
              </StatNumber>
              <StatHelpText>{new Date().toLocaleDateString()}</StatHelpText>
            </Stat>
          </SimpleGrid>
        )}

        {/* Goal Progress Bar */}
        {poll.response_goal && progress && (
          <Box bg={boxBgColor} p={4} borderRadius="md" border="1px solid" borderColor={borderColor} mb={6}>
            <Text fontSize="sm" fontWeight="bold" mb={2}>
              Progress to Goal: {progress.total_completed} / {poll.response_goal}
            </Text>
            <Progress
              value={progress.progress_percentage}
              colorScheme={progress.goal_met ? 'green' : 'blue'}
              size="lg"
              borderRadius="md"
            />
          </Box>
        )}

        {/* Demographic Comparison */}
        {comparisonData.length > 0 && (
          <Box bg={boxBgColor} p={6} borderRadius="md" border="1px solid" borderColor={borderColor} mb={6}>
            <Heading as="h2" size="md" mb={4}>
              Demographic Balance (Target vs. Current)
            </Heading>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="group" angle={-45} textAnchor="end" height={120} />
                <YAxis label={{ value: 'Percentage', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="target" fill="#0088FE" name="Target %" />
                <Bar dataKey="current" fill="#00C49F" name="Current %" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {/* A/B Test Results */}
        {abResults && abResults.variants && abResults.variants.length > 0 && (
          <Box bg={boxBgColor} p={6} borderRadius="md" border="1px solid" borderColor={borderColor} mb={6}>
            <Heading as="h2" size="md" mb={4}>
              A/B Test Results
            </Heading>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Variant</Th>
                  <Th>Text Preview</Th>
                  <Th isNumeric>Sent</Th>
                  <Th isNumeric>Completed</Th>
                  <Th isNumeric>Rate</Th>
                </Tr>
              </Thead>
              <Tbody>
                {abResults.variants.map((v) => {
                  const isLeading = abResults.variants.length > 1 &&
                    v.completion_rate === Math.max(...abResults.variants.map(x => x.completion_rate)) &&
                    v.completion_rate > 0;
                  return (
                    <Tr key={v.label} bg={isLeading ? 'green.50' : undefined}>
                      <Td fontWeight="bold">
                        {v.label}
                        {isLeading && <Badge ml={2} colorScheme="green" fontSize="xs">Leading</Badge>}
                      </Td>
                      <Td fontSize="xs" color="gray.600" maxW="300px" isTruncated>{v.text_preview}</Td>
                      <Td isNumeric>{v.sent}</Td>
                      <Td isNumeric>{v.completed}</Td>
                      <Td isNumeric fontWeight={isLeading ? 'bold' : 'normal'}>{v.completion_rate}%</Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        )}

        {/* Tranche History */}
        <Box bg={boxBgColor} p={6} borderRadius="md" border="1px solid" borderColor={borderColor} mb={6}>
          <Heading as="h2" size="md" mb={4}>
            Tranche History
          </Heading>
          {tranches.length === 0 ? (
            <Text color="gray.600">No tranches generated yet. Click "Generate Next Tranche" to begin.</Text>
          ) : (
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Tranche</Th>
                  <Th isNumeric>Sent</Th>
                  <Th isNumeric>Completed</Th>
                  <Th isNumeric>Completion Rate</Th>
                </Tr>
              </Thead>
              <Tbody>
                {tranches.map((tranche) => (
                  <Tr key={tranche.tranche}>
                    <Td fontWeight="bold">Tranche {tranche.tranche}</Td>
                    <Td isNumeric>{tranche.sent}</Td>
                    <Td isNumeric>{tranche.completed}</Td>
                    <Td isNumeric>{tranche.completion_rate}%</Td>
                  </Tr>
                ))}
                <Tr fontWeight="bold" bg="gray.50">
                  <Td>Total</Td>
                  <Td isNumeric>{tranches.reduce((sum, t) => sum + t.sent, 0)}</Td>
                  <Td isNumeric>{tranches.reduce((sum, t) => sum + t.completed, 0)}</Td>
                  <Td isNumeric>
                    {tranches.length > 0
                      ? ((tranches.reduce((sum, t) => sum + t.completed, 0) /
                          tranches.reduce((sum, t) => sum + t.sent, 0)) *
                          100
                        ).toFixed(1)
                      : 0}
                    %
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          )}
        </Box>

        {/* Survey Invitations (for testing) */}
        <Box bg={boxBgColor} p={6} borderRadius="md" border="1px solid" borderColor={borderColor} mb={6}>
          <HStack justify="space-between" mb={4}>
            <Heading as="h2" size="md">
              Survey Invitations (Testing)
            </Heading>
            <Button onClick={handleToggleRespondents} size="sm">
              {showRespondents ? 'Hide' : 'Show'} Invitations
            </Button>
          </HStack>

          {showRespondents && (
            <>
              {respondents.length === 0 ? (
                <Text color="gray.600">No invitations generated yet.</Text>
              ) : (
                <>
                  <Text fontSize="sm" color="gray.600" mb={4}>
                    Click any link to open the survey in a new tab for testing. Use incognito mode to test multiple responses.
                  </Text>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Tranche</Th>
                        <Th>Phone Number</Th>
                        <Th>Survey Link</Th>
                        <Th>Completed</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {respondents.map((respondent) => (
                        <Tr key={respondent.id}>
                          <Td>{respondent.tranche}</Td>
                          <Td fontFamily="mono" fontSize="sm">{respondent.phone_number}</Td>
                          <Td>
                            {respondent.unique_link ? (
                              <Button
                                as="a"
                                href={`/survey/respond/${respondent.unique_link}`}
                                target="_blank"
                                size="xs"
                                colorScheme="blue"
                              >
                                Open Survey
                              </Button>
                            ) : (
                              <Text fontSize="xs" color="gray.500">No link</Text>
                            )}
                          </Td>
                          <Td>
                            <Badge colorScheme={respondent.has_completed ? 'green' : 'gray'}>
                              {respondent.has_completed ? 'Yes' : 'No'}
                            </Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </>
              )}
            </>
          )}
        </Box>

        {/* Navigation */}
        <HStack spacing={2}>
          <Button as={ReactRouterLink} to={`/manage-questions/${pollId}`} variant="outline">
            Back to Poll
          </Button>
          <Button as={ReactRouterLink} to={`/poll-results/${pollId}`} variant="outline">
            View Results
          </Button>
        </HStack>
      </Box>

      {/* Generate Tranche Modal */}
      {poll && audience && (
        <GenerateTrancheModal
          isOpen={isOpen}
          onClose={onClose}
          poll={poll}
          audience={audience}
          onSuccess={handleTrancheGenerated}
          isFirstTranche={tranches.length === 0}
        />
      )}
    </Box>
  );
}

export default LivePollingDashboard;
